/**
 * Générateur — Il construit une figure en créant un motif et en le
 * reproduisant à l'aide d'une boucle.
 * Attendu ID : 251 | Niveau : 3ème | Domaine : Algorithmique (d21, O2 niv. 3)
 *
 * Source doc prof (Créé n°19 — banque 3e). Plan auto-validé 2026-07-18 :
 * « polygone » (que trace « répéter n fois [avancer ; tourner de 360/n] »,
 * QCM), « formule » (compléter le script d'un polygone + l'angle 360/n pour
 * un n donné), « rosace » (répéter k fois [Motif ; tourner de 360/k] →
 * qu'obtient-on, QCM — croisement avec les rotations d'O4 Espace).
 */

import { randomChoice, randInt, shuffle, tex, makeQcm, makeTexteTrou } from '../../../utils';

const ATTENDU_ID = 251;
const L_POLYGONE = 'Que trace ce script ?';
const L_FORMULE = 'L\'angle 360/n';
const L_ROSACE = 'La rosace de motifs';
const opts = (label) => ({ niveau: '3eme', matiere: 'maths', attendu: ATTENDU_ID, label });

const POLYGONES = [
    [3, 'un triangle équilatéral'], [4, 'un carré'], [5, 'un pentagone régulier'],
    [6, 'un hexagone régulier'], [8, 'un octogone régulier'], [10, 'un décagone régulier'],
];

// ═════════ « polygone » — QCM ═════════

const genPolygoneQcm = () => {
    const [n, nom] = randomChoice(POLYGONES);
    const c = randInt(3, 8) * 10;
    const autres = shuffle(POLYGONES.filter(p => p[0] !== n)).slice(0, 2).map(p => p[1]);
    const payload = makeQcm([nom, ...autres, 'une étoile'], nom,
        `<p><code>répéter ${n} fois [avancer de ${c} ; tourner ↻ de ${360 / n} degrés]</code></p>`
        // La règle « angle extérieur, 360° au total » est exactement ce que la
        // carte fait découvrir ; « (le tracé se croise) » ne glosait qu'un choix.
        + '<p><b>Que trace ce script ?</b></p>',
        opts(L_POLYGONE));
    payload._meta.params = { n, c, nom };
    return payload;
};

// ═════════ « formule » ═════════

const genFormuleTrous = () => {
    let n1; let c; let n2;
    for (let essai = 0; essai < 30; essai++) {
        [n1] = randomChoice(POLYGONES.filter(p => p[0] !== 4));  // 4 → tourner 90 = trop vu
        c = randInt(3, 8) * 10;
        n2 = randomChoice([5, 8, 9, 10, 12, 15].filter(v => v !== n1));
        const vals = [n1, c, 360 / n1, 360 / n2];
        if (new Set(vals).size === 4) break;
    }
    const nom1 = POLYGONES.find(p => p[0] === n1)[1];
    const phrase = `répéter {{${n1}}} fois [avancer de ${c} ; tourner ↻ de {{${360 / n1}}} degrés] ; `
        + `pour ${n2} côtés, on tournerait de {{${360 / n2}}} degrés.`;
    const payload = makeTexteTrou(phrase,
        `<p>On veut tracer ${nom1} de côté ${c} pas avec une boucle, puis généraliser : pour un polygone `
        + `régulier à n côtés, le lutin tourne à chaque sommet de ${tex('\\dfrac{360}{n}')} degrés.</p>`
        + `<p><b>Complète le script, puis donne l'angle pour ${n2} côtés.</b></p>`,
        { ...opts(L_FORMULE), mode: 'saisie' });
    payload._meta.params = { n1, c, n2 };
    return payload;
};

// ═════════ « rosace » — QCM ═════════

const genRosaceQcm = () => {
    const k = randomChoice([5, 6, 8, 9, 10, 12]);
    const motif = randomChoice(['Hexagone', 'Carré', 'Losange', 'Triangle']);
    const correct = `une rosace de ${k} ${motif.toLowerCase()}s autour d'un point`;
    const payload = makeQcm([correct,
        `un seul ${motif.toLowerCase()}, retracé ${k} fois au même endroit`,
        `une frise de ${k} ${motif.toLowerCase()}s alignés`,
        'rien : le script plante'], correct,
        `<p>Le bloc « ${motif} » trace un ${motif.toLowerCase()} puis revient à son point de départ, dans `
        + 'sa direction de départ.</p>'
        + `<p><code>répéter ${k} fois [${motif} ; tourner ↻ de ${360 / k} degrés]</code></p>`
        // « Chaque motif est l'image du précédent par une rotation » énonçait la
        // conclusion : c'est la lecture géométrique du script qui est évaluée.
        + '<p><b>Qu\'obtient-on ?</b></p>',
        opts(L_ROSACE));
    payload._meta.params = { k, motif };
    return payload;
};

const GENERATORS = {
    polygone_qcm_unique: genPolygoneQcm,
    formule_texte_trou_saisie: genFormuleTrous,
    rosace_qcm_unique: genRosaceQcm,
};
const FORMAT_LABELS = { qcm_unique: 'QCM', texte_trou_saisie: 'Saisie trous' };
const buildFormats = (prefix) =>
    Object.keys(GENERATORS).filter(k => k.startsWith(`${prefix}_`))
        .map(k => ({ key: k, label: FORMAT_LABELS[k.slice(prefix.length + 1)] || k }));
export const CATEGORIES = [
    { id: 'polygone', label: L_POLYGONE, formatsList: buildFormats('polygone') },
    { id: 'formule', label: L_FORMULE, formatsList: buildFormats('formule') },
    { id: 'rosace', label: L_ROSACE, formatsList: buildFormats('rosace') },
];
export const generate = (typeExercice) => {
    let key;
    if (typeExercice && GENERATORS[typeExercice]) key = typeExercice;
    else if (typeExercice) {
        const matching = Object.keys(GENERATORS).filter(k => k.startsWith(typeExercice) || k.includes(typeExercice));
        key = matching.length ? randomChoice(matching) : randomChoice(Object.keys(GENERATORS));
    } else key = randomChoice(Object.keys(GENERATORS));
    const payload = GENERATORS[key]();
    payload._meta.generatorKey = key;
    return payload;
};
